// VAT Returns data layer (Phase 4, P4-CC18) — the live filed-return record.
//
// The SINGLE interface between the app and public.vat_returns
// (supabase/migrations/20260805120000_vat_returns.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id — trg_10_company owns it, forced from the
//     caller's own membership. House rule, stated at the top of src/lib/team.js.
//   • A blocked UPDATE/DELETE (RLS WITH CHECK / USING failing) matches ZERO
//     ROWS SILENTLY — Postgres does not raise for this. Every update here uses
//     .select(SELECT).single(), so a blocked write surfaces as PGRST116 (the
//     "expected exactly one row" failure), never a false success. The one
//     delete uses .select("id") and asserts the returned array is non-empty,
//     for the same reason.
//   • canWriteVat() asks the database (can_write('vat') via RPC) — never
//     infers from a role. Mirrors canWriteInventory() (src/lib/inventory.js).
//     THROWS rather than swallows an RPC failure; the caller must treat that
//     as NOT PERMITTED (fail closed) — a false "you may not" costs a support
//     question, a false "you may" hands someone a button that always fails.
//
// THE FILED-RETURN LOCK (enforce_vat_return_filed_lock(), in the migration):
// once a return leaves 'draft', its figures are frozen — only status,
// reference_number and notes may still change, and a return REJECTED by
// Revenue is the only one that may return to draft. Violations arrive as
// P0001, deliberately NOT 42501 (see the migration's own comment for why —
// a filed-lock violation is not a permission problem, and conflating the two
// sends an accountant with full permission looking in the wrong place).
// toError() below passes a P0001 message through verbatim: it was written
// for the person reading it.
//
// RETURN-TYPE CONTRACT (consistent with the other modules):
//   collection → Array · single → T|null · create/update → the affected T ·
//   delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { getVatSalesBreakdown } from "./invoices";
import { getVatPurchasesBreakdown } from "./expenses";

const T = "vat_returns";

export const VAT_RETURN_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];
export const RETURN_PERIODS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

// submitted_at is deliberately absent — database-owned by the filed-return
// lock trigger on every path; a client sending it would either be silently
// dropped for an insert (see createVatReturn) or, on an update, would trip
// the filed-lock's own figures-locked check the moment the row is no longer
// a draft. id/created_at/updated_at are likewise never client-supplied.
const WRITE_FIELDS = [
  "return_period", "period_start", "period_end", "status", "reference_number",
  "sales_total_ex_vat", "sales_0_rate", "sales_9_rate", "sales_13_5_rate", "sales_23_rate",
  "total_vat_on_sales", "purchases_total_ex_vat", "total_vat_recoverable",
  "reverse_charge_amount", "calculated_at", "notes",
];
const SELECT =
  "id, return_period, period_start, period_end, status, reference_number, " +
  "sales_total_ex_vat, sales_0_rate, sales_9_rate, sales_13_5_rate, sales_23_rate, total_vat_on_sales, " +
  "purchases_total_ex_vat, total_vat_recoverable, vat_payable_t3, vat_repayable_t4, reverse_charge_amount, " +
  "calculated_at, submitted_at, notes, created_at, updated_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    // RLS denial ONLY. Getting this right is what makes 42501 mean exactly
    // one thing in this file — see the filed-lock's P0001 below.
    case "42501":
      message = "You do not have permission to change VAT returns.";
      break;
    case "23P01":
      message = "A VAT return already exists covering some or all of these dates.";
      break;
    case "23514": {
      // Four CHECK constraints on this table can all raise 23514 — surface
      // which one, not the raw Postgres text.
      const msg = (error && error.message) || "";
      if (msg.includes("vat_returns_dates_chk")) message = "The period end date cannot be before the period start date.";
      else if (msg.includes("vat_returns_period_chk")) message = "Return period must be 'monthly' or 'quarterly'.";
      else if (msg.includes("vat_returns_status_chk")) message = "Invalid VAT return status.";
      else if (msg.includes("vat_returns_submitted_at_chk")) message = "A submitted VAT return must have a submission date.";
      else message = "Invalid value for a VAT return field.";
      break;
    }
    case "23502":
      message = "A required field is missing.";
      break;
    // The filed-return lock (enforce_vat_return_filed_lock) — deliberately
    // P0001, not 42501 (see the migration). Its messages are written for the
    // end user; pass them through exactly as raised, never re-worded here.
    case "P0001":
      message = (error && error.message) || fallback;
      break;
    case "PGRST116":
      message = "VAT return not found, or you do not have permission to change it.";
      break;
    case "TOO_MANY_ROWS":
      message = (error && error.message) || fallback;
      break;
    default:
      message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Permission check ─────────────────────────────────────────────────────────

/**
 * Whether the current user may write vat_returns directly — owner/admin/
 * accountant per the matrix; sales/driver/employee neither. Mirrors
 * canWriteInventory() (src/lib/inventory.js) exactly: THROWS rather than
 * swallows an RPC failure, so the caller can fail closed.
 * @returns {Promise<boolean>}
 */
export async function canWriteVat() {
  const { data, error } = await client().rpc("can_write", { p_resource: "vat" });
  if (error) throw toError(error, "Could not check VAT return permissions");
  return data === true;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List VAT returns (RLS-scoped), most recent period first.
 * @returns {Promise<Array>}
 */
export async function listVatReturns() {
  const { data, error } = await client().from(T).select(SELECT).order("period_start", { ascending: false });
  if (error) throw toError(error, "Could not load VAT returns");
  return data || [];
}

/**
 * A single VAT return, or null.
 * @param {string} id
 */
export async function getVatReturn(id) {
  const { data, error } = await client().from(T).select(SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load VAT return");
  return data || null;
}

// ── Calculation ──────────────────────────────────────────────────────────────
// Lives here, not in the UI component — CC18 Phase 3 must call this, not
// re-derive the figures itself.

/**
 * Derive the VAT return figures for [start,end] from the live Supabase
 * ledgers (getVatSalesBreakdown + getVatPurchasesBreakdown). Does NOT catch
 * either call's errors — a failed calculation must throw and stop the caller
 * from proceeding, never silently become zeros on a form someone is about to
 * file with Revenue. Both source functions now throw loudly (TOO_MANY_ROWS)
 * instead of silently truncating past 10,000 rows in a period.
 * @param {string} start ISO date @param {string} end ISO date
 * @returns {Promise<Object>} the return's figures + calculated_at (ISO timestamp)
 */
export async function calculateVatReturn(start, end) {
  const [sales, purchases] = await Promise.all([
    getVatSalesBreakdown(start, end),
    getVatPurchasesBreakdown(start, end),
  ]);
  return {
    sales_total_ex_vat: sales.sales_total_ex_vat,
    sales_0_rate: sales.sales_0,
    sales_9_rate: sales.sales_9,
    sales_13_5_rate: sales.sales_13_5,
    sales_23_rate: sales.sales_23,
    total_vat_on_sales: sales.total_vat_on_sales,
    purchases_total_ex_vat: purchases.purchases_total_ex_vat,
    total_vat_recoverable: purchases.total_vat_reclaimable,
    // vat_payable_t3 / vat_repayable_t4 are NOT set here — they're generated
    // columns (public.vat_returns migration 20260807120000), derived by the
    // database from total_vat_on_sales/total_vat_recoverable. The client
    // cannot write them and must not compute its own copy to send.
    calculated_at: new Date().toISOString(),
  };
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create a VAT return. Never sends company_id (trg_10_company owns it) or
 * submitted_at (the filed-lock trigger owns it).
 * @param {Object} payload
 * @returns {Promise<Object>} the created row
 */
export async function createVatReturn(payload) {
  const { data, error } = await client().from(T).insert(pick(payload, WRITE_FIELDS)).select(SELECT).single();
  if (error) throw toError(error, "Could not create VAT return");
  return data;
}

/**
 * Update a VAT return. A blocked write — RLS, or the filed-return lock —
 * never silently succeeds here: .select(SELECT).single() turns a zero-row
 * RLS block into PGRST116, and the lock trigger's own raise arrives as a
 * genuine error (P0001) either way.
 * @param {string} id @param {Object} payload
 * @returns {Promise<Object>} the updated row
 */
export async function updateVatReturn(id, payload) {
  const { data, error } = await client().from(T).update(pick(payload, WRITE_FIELDS)).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update VAT return");
  return data;
}

/**
 * Delete a VAT return. Drafts delete freely; a filed return's delete is
 * blocked by the DB trigger (arrives as a genuine P0001 error). An RLS block
 * instead matches zero rows with NO error — .select("id") + an explicit
 * empty-array check is what turns that silent case into a real failure.
 * @param {string} id
 */
export async function deleteVatReturn(id) {
  const { data, error } = await client().from(T).delete().eq("id", id).select("id");
  if (error) throw toError(error, "Could not delete VAT return");
  if (!data || data.length === 0) {
    const e = new Error("VAT return not found, or you do not have permission to delete it.");
    e.code = "PGRST116";
    throw e;
  }
}
